import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Grid, Stack } from '@mui/material'
import {
  checkCertificateRuleName,
  createCertificateRule,
  editCertificateRule,
} from 'api/certificates'
import Breadcrumbs from 'components/Breadcumbs'
import FormInput from 'components/form-elements/FormInput'
import FormMultipleSelect from 'components/form-elements/FormMultipleSelect'
import NotificationSys from 'components/NotificationSystem'
import { FIELD_IS_REQUIRED } from 'const'
import { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { CertificateRule, CertificateRuleType, Nullish, Option } from 'types'
import { getRoute } from 'utils'
import { array, number, object, string, TypeOf } from 'zod'

export function CertificatesEditPageForm({
  data,
  coursesOptions,
  lessonsOptions,
  setData,
  setLoading,
}: {
  data: Nullish<CertificateRule>
  coursesOptions: Option[]
  lessonsOptions: Option[]
  setData: (certificate: CertificateRule) => void
  setLoading: (value: boolean) => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const isAddPage = location.pathname.includes('add')

  const formSchema = object({
    name: string().min(1, FIELD_IS_REQUIRED),
    courses: array(
      object({
        label: string(),
        value: number(),
      }),
    ).optional(),
    lessons: array(
      object({
        label: string(),
        value: number(),
      }),
    ).optional(),
    notes: string().optional(),
  }).refine(
    (data) => {
      const hasCourses = data.courses && data.courses.length > 0
      const hasLessons = data.lessons && data.lessons.length > 0
      return hasCourses || hasLessons
    },
    {
      message: 'Either "Courses" or "Lessons" must be filled.',
      path: ['courses'],
    },
  )

  type FormType = TypeOf<typeof formSchema>

  const defaultCourses = useMemo(() => {
    if (!data) return []

    return data.courseIds
      .map((courseId) => {
        const course = coursesOptions.find((course) => course.value === courseId)
        return course
          ? { label: course.label, value: course.value as number }
          : { label: '', value: 0 }
      })
      .filter((option) => option.label !== '')
  }, [coursesOptions, data])

  const defaultLessons = useMemo(() => {
    if (!data) return []

    return data.lessonIds
      .map((lessonId) => {
        const lesson = lessonsOptions.find((lessonItem) => lessonItem.value === lessonId)
        return lesson
          ? { label: lesson.label, value: lesson.value as number }
          : { label: '', value: 0 }
      })
      .filter((option) => option.label !== '')
  }, [data, lessonsOptions])

  const methods = useForm<FormType>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: data?.name || '',
      courses: defaultCourses,
      lessons: defaultLessons,
      notes: data?.note || '',
    },
  })

  const selectedCourses = methods.watch('courses')
  const selectedLessons = methods.watch('lessons')

  useEffect(() => {
    if (!selectedCourses || !selectedLessons) return

    const hasCourses = selectedCourses?.length > 0
    const hasLessons = selectedLessons?.length > 0

    if (!hasCourses && !hasLessons) {
      methods.setError('courses', {
        type: 'manual',
        message: 'Either "Courses" or "Lessons" must be filled.',
      })
    } else {
      methods.clearErrors('courses')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourses, selectedLessons])

  const onSubmit = useCallback(
    async (values: FormType) => {
      try {
        setLoading(true)

        const courseIds =
          values.courses && values.lessons?.length === 0
            ? values.courses.map((course) => course.value)
            : []
        const lessonIds =
          values.lessons && values.courses?.length === 0
            ? values.lessons.map((lesson) => lesson.value)
            : []

        const type =
          values.lessons && values.lessons.length > 0
            ? CertificateRuleType.BY_LESSONS
            : CertificateRuleType.BY_COURSES

        const request = {
          name: values.name,
          type,
          courseIds,
          lessonIds,
          note: values.notes,
        }

        const needNameValidation = isAddPage || (data && data.name !== values.name)

        if (needNameValidation) {
          const isValid = await checkCertificateRuleName(values.name)
          if (!isValid) {
            methods.setError('name', {
              type: 'manual',
              message: 'Enter unique name',
            })

            NotificationSys.showWarning(
              'Name is taken already. Enter unique name of the certificate rule.',
            )
            return
          }
        }

        if (!isAddPage && data) {
          const newData = await editCertificateRule({
            id: data.id,
            ...request,
          })
          setData(newData.data)
        } else {
          const newData = await createCertificateRule(request)
          navigate(getRoute(routes.certificatesEdit, { id: newData.data.id }))
        }

        NotificationSys.showSuccess('Certificate saved successfully')
      } catch (e) {
        throw new Error('An error occurred while saving the Certificate')
      } finally {
        setLoading(false)
      }
    },
    [data, isAddPage, navigate, setData, setLoading, methods],
  )

  const onCancel = useCallback(() => {
    navigate(routes.certificates)
  }, [navigate])

  const path = [
    {
      href: routes.certificates,
      text: 'Certificates',
    },
    { text: isAddPage ? 'Add Certificate' : data?.name || 'Edit Certificate' },
  ]

  return (
    <div>
      <Breadcrumbs path={path} />

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          noValidate
          width="100%"
          gap={3}
          mt={4}
        >
          <Grid container spacing={4}>
            <Grid item sm={12} md={12} lg={6} xl={6}>
              <Stack spacing={2}>
                <FormInput label="Certificate Name" name="name" required fullWidth />

                <FormMultipleSelect
                  name="courses"
                  label="Select Courses"
                  options={coursesOptions || []}
                  isLoading={false}
                  defaultValue={[]}
                  disabled={selectedLessons && selectedLessons.length > 0}
                />

                <FormMultipleSelect
                  name="lessons"
                  label="Select Lesson"
                  options={lessonsOptions || []}
                  isLoading={false}
                  defaultValue={[]}
                  disabled={selectedCourses && selectedCourses.length > 0}
                />

                <FormInput
                  name="notes"
                  label="Note"
                  placeholder="Start typing"
                  fullWidth
                  minRows={3}
                  maxRows={6}
                  multiline
                  checkTouchField={false}
                />
              </Stack>
            </Grid>

            <Grid item sm={12} md={12} lg={12} xl={12}>
              <Stack direction="row" spacing={2}>
                <LoadingButton
                  disabled={false}
                  loading={false}
                  variant="contained"
                  type="submit"
                  sx={{ width: '120px' }}
                >
                  Save
                </LoadingButton>

                <Button variant="outlined" onClick={onCancel} sx={{ width: '120px' }}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </div>
  )
}
